const STATUT_LABEL = { a_faire: 'À faire', en_cours: 'En cours', terminee: 'Terminée' };
const STATUT_STYLE = {
  a_faire: 'bg-white/10 text-white/70',
  en_cours: 'bg-sky-500/20 text-sky-300',
  terminee: 'bg-emerald-500/20 text-emerald-300',
};

const KpiBlock = ({ items }) => (
  <div className={`grid gap-2 ${items.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`} data-testid="agent-ui-kpi">
    {items.map((k, i) => (
      <div key={i} className="rounded-xl bg-white/5 border border-white/10 p-3">
        <div className="text-[10px] uppercase tracking-wide text-white/40 font-bold truncate">{k.label}</div>
        <div className="text-lg font-extrabold text-white mt-0.5">{k.value}</div>
        {k.delta && <div className={`text-[11px] font-semibold mt-0.5 ${k.delta.trim().startsWith('-') ? 'text-rose-400' : 'text-emerald-400'}`}>{k.delta}</div>}
      </div>
    ))}
  </div>
);

const TasksBlock = ({ titre, items }) => (
  <div className="rounded-xl bg-white/5 border border-white/10 p-3" data-testid="agent-ui-tasks">
    {titre && <div className="text-[10px] uppercase tracking-wide text-white/40 font-bold mb-2">{titre}</div>}
    <div className="space-y-1.5">
      {items.map((t, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${t.priorite === 'haute' ? 'bg-rose-500' : 'bg-white/30'}`} />
          <span className={`flex-1 text-white/85 ${t.statut === 'terminee' ? 'line-through text-white/40' : ''}`}>{t.titre}</span>
          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${STATUT_STYLE[t.statut]}`}>{STATUT_LABEL[t.statut]}</span>
        </div>
      ))}
    </div>
  </div>
);

const ChartBlock = ({ titre, chart, data }) => {
  const max = Math.max(...data.map((d) => d.value), 1);
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 p-3" data-testid="agent-ui-chart">
      {titre && <div className="text-[10px] uppercase tracking-wide text-white/40 font-bold mb-2">{titre}</div>}
      {chart === 'line' ? (
        <svg viewBox="0 0 100 40" className="w-full h-20" preserveAspectRatio="none">
          <polyline fill="none" stroke="#fb923c" strokeWidth="1.5"
            points={data.map((d, i) => `${data.length > 1 ? (i / (data.length - 1)) * 96 + 2 : 50},${38 - (d.value / max) * 32}`).join(' ')} />
          {data.map((d, i) => (
            <circle key={i} cx={data.length > 1 ? (i / (data.length - 1)) * 96 + 2 : 50} cy={38 - (d.value / max) * 32} r="1.4" fill="#fb923c" />
          ))}
        </svg>
      ) : (
        <div className="flex items-end gap-1 h-20">
          {data.map((d, i) => (
            <div key={i} className="flex-1 flex flex-col items-center min-w-0" title={`${d.label}: ${d.value}`}>
              <div className="w-full rounded-t bg-gradient-to-t from-orange-600 to-orange-400"
                style={{ height: `${Math.max((d.value / max) * 100, 3)}%` }} />
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-1 mt-1">
        {data.map((d, i) => (
          <div key={i} className="flex-1 text-center text-[8px] text-white/40 truncate">{d.label}</div>
        ))}
      </div>
    </div>
  );
};

const TableBlock = ({ titre, columns, rows }) => (
  <div className="rounded-xl bg-white/5 border border-white/10 p-3 overflow-x-auto" data-testid="agent-ui-table">
    {titre && <div className="text-[10px] uppercase tracking-wide text-white/40 font-bold mb-2">{titre}</div>}
    <table className="w-full text-xs">
      <thead>
        <tr>{columns.map((c, i) => <th key={i} className="text-left text-white/40 font-bold pb-1.5 pr-2">{c}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-t border-white/5">
            {r.map((c, j) => <td key={j} className={`py-1.5 pr-2 ${j === 0 ? 'text-white/85' : 'text-white/60'}`}>{c}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ActionsBlock = ({ items, onAction }) => (
  <div className="flex flex-wrap gap-2" data-testid="agent-ui-actions">
    {items.map((a, i) => (
      <button key={i} onClick={() => onAction?.(a.message)} data-testid={`agent-ui-action-${i}`}
        className="text-[12px] font-semibold px-3 py-1.5 rounded-full border border-orange-400/40 bg-orange-500/10 text-orange-300 hover:bg-orange-500/25 transition-colors">
        {a.label}
      </button>
    ))}
  </div>
);

export const AgentUiBlocks = ({ blocks, onAction }) => {
  if (!blocks?.length) return null;
  return (
    <div className="space-y-2 max-w-[95%]" data-testid="agent-ui-blocks">
      {blocks.map((b, i) => {
        if (b.type === 'kpi') return <KpiBlock key={i} items={b.items} />;
        if (b.type === 'tasks') return <TasksBlock key={i} titre={b.titre} items={b.items} />;
        if (b.type === 'chart') return <ChartBlock key={i} titre={b.titre} chart={b.chart} data={b.data} />;
        if (b.type === 'table') return <TableBlock key={i} titre={b.titre} columns={b.columns} rows={b.rows} />;
        if (b.type === 'actions') return <ActionsBlock key={i} items={b.items} onAction={onAction} />;
        return null;
      })}
    </div>
  );
};
